import { makeAutoObservable, runInAction } from "mobx"
import { track } from "../../analytics"
import { queueModeManager } from "../../components/sidebar/queueModeManager"
import { blockedFeatureForCreation, describeBlockedFeature } from "../../domain/nestFeatureSupport"
import { nestClient, requestNestMethod } from "../../nest/client"
import { userInputTurnInput } from "../../nest/turnInput"
import { getManagedWorktreeBasePath, getManagedWorktreePath, gitApi } from "../../nestAPIs/git"
import type { TaskAgentGitContext } from "../../persistence/records"
import { parseTaskRecord } from "../../persistence/records"
import { requestAppOperation, readAgentAccess, setAgentAccess } from "../../wildlark/appClient"
import type { HarnessId } from "../../providerEvents/harnessEventTypes"
import { CLIENT_RENDERING_PREAMBLE } from "../../prompts/preamble"
import type { ImageAttachment, UserInputContext } from "../../types"
import { getDeviceId } from "../../utils/deviceId"
import { ulid } from "../../utils/ulid"
import { requestWildLarkMethod } from "../../wildlark/client"
import type { ThinkingLevel } from "../TaskModel"
import type { CodeStore } from "../store"

export interface TaskCreationOptions {
    repoId: string
    description: string
    recoveryDraftId?: string
    images?: ImageAttachment[]
    enabledMcpServerIds?: string[]
    harnessId?: HarnessId
    modelId?: string
    thinking?: ThinkingLevel
    fastMode?: boolean
    chiefOfStaff?: boolean
}

export interface TaskCreation {
    id: string
    repoId: string
    description: string
    recoveryDraftId?: string
    images: ImageAttachment[]
    enabledMcpServerIds?: string[]
    harnessId?: HarnessId
    modelId?: string
    thinking?: ThinkingLevel
    fastMode?: boolean
    chiefOfStaff?: boolean
    phase: "pending" | "completing"
    error: string | null
    abortController: AbortController
    createdAt: string
    completedTaskId: string | null
}

export function buildTaskCreationInput(description: string, images: ImageAttachment[]): UserInputContext {
    return {
        userInput: description,
        images: images.map(cloneImageAttachment),
    }
}

function cloneImageAttachment(image: ImageAttachment): ImageAttachment {
    return {
        id: image.id,
        ...(image.attachmentId ? { attachmentId: image.attachmentId } : {}),
        mediaType: image.mediaType,
        ext: image.ext,
        originalWidth: image.originalWidth,
        originalHeight: image.originalHeight,
        resizedWidth: image.resizedWidth,
        resizedHeight: image.resizedHeight,
    }
}

export class TaskCreationManager {
    creationsById: Map<string, TaskCreation> = new Map()

    constructor(private store: CodeStore) {
        makeAutoObservable(this, {
            creationsById: true,
        })
    }

    newTask(options: TaskCreationOptions): string {
        const id = ulid()
        const creation: TaskCreation = {
            id,
            repoId: options.repoId,
            description: options.description,
            recoveryDraftId: options.recoveryDraftId,
            images: options.images ? [...options.images] : [],
            enabledMcpServerIds: options.enabledMcpServerIds,
            harnessId: options.harnessId,
            modelId: options.modelId,
            thinking: options.thinking,
            fastMode: options.fastMode,
            chiefOfStaff: options.chiefOfStaff,
            phase: "pending",
            error: null,
            abortController: new AbortController(),
            createdAt: new Date().toISOString(),
            completedTaskId: null,
        }

        runInAction(() => {
            this.creationsById.set(id, creation)
        })

        this.runCreation(id)

        return id
    }

    getCreation(id: string): TaskCreation | null {
        return this.creationsById.get(id) || null
    }

    getCreationsForRepo(repoId: string): TaskCreation[] {
        return Array.from(this.creationsById.values()).filter((c) => c.repoId === repoId && c.completedTaskId === null)
    }

    async cancelCreation(id: string): Promise<void> {
        const creation = this.creationsById.get(id)
        if (!creation) return

        creation.abortController.abort()
        this.restoreRecoveryDraft(creation)

        runInAction(() => {
            this.creationsById.delete(id)
        })
    }

    retryCreation(id: string): void {
        const creation = this.creationsById.get(id)
        if (!creation || !creation.error) return

        runInAction(() => {
            creation.error = null
            creation.phase = "pending"
            creation.abortController = new AbortController()
        })

        this.runCreation(id)
    }

    async dismissCreation(id: string): Promise<void> {
        await this.cancelCreation(id)
    }

    private async runCreation(id: string): Promise<void> {
        const creation = this.creationsById.get(id)
        if (!creation) return

        const repo = this.store.repos.getRepo(creation.repoId)
        if (!repo) {
            runInAction(() => {
                creation.error = "Repository not found"
            })
            return
        }

        const signal = creation.abortController.signal

        try {
            if (signal.aborted) throw new Error("Task creation cancelled")

            runInAction(() => {
                creation.phase = "completing"
            })

            const blockedFeature = blockedFeatureForCreation(creation)
            if (blockedFeature) throw new Error(describeBlockedFeature(blockedFeature))

            const agentGitContext = await this.designateAgentGitContext(creation, repo.path)
            const created = await requestAppOperation(
                "task.create",
                {
                    workspaceId: creation.repoId,
                    description: creation.description,
                    harnessId: creation.harnessId ?? "claude-code",
                    model: creation.modelId,
                    thinking: creation.thinking,
                    clientRequestId: creation.id,
                    agentGitContext,
                    enabledMcpServerIds: creation.enabledMcpServerIds,
                },
                { signal }
            )
            const initialRecord = parseTaskRecord(created.task)
            if (!initialRecord) throw new Error("The computer returned an invalid task record")
            const productState = this.store.requireProductState()
            await productState.refreshTask(initialRecord.id, creation.repoId)
            if (creation.chiefOfStaff) {
                const access = await readAgentAccess(initialRecord.id, { signal })
                await setAgentAccess(
                    {
                        taskId: initialRecord.id,
                        workspaceId: creation.repoId,
                        sessionId: initialRecord.id,
                        modules: access.available.map((module) => module.id),
                        role: "chief-of-staff",
                        expectedVersion: access.version,
                    },
                    { signal }
                )
            }

            const turnResult = await requestWildLarkMethod(
                "wildlark.task.turn.start",
                {
                    taskId: initialRecord.id,
                    workspaceId: creation.repoId,
                    sessionId: initialRecord.id,
                    input: userInputTurnInput(buildTaskCreationInput(creation.description, creation.images)),
                    disposition: "enqueue",
                    clientRequestId: creation.id,
                    prompt: {
                        clientPreamble: CLIENT_RENDERING_PREAMBLE,
                        ...(agentGitContext ? { designatedWorktreePath: agentGitContext.worktreePath } : {}),
                    },
                    options: {
                        mode: "full",
                        permissionMode: this.store.personalSettingsStore?.settings.current.permissionMode ?? "yolo",
                        model: creation.modelId,
                        providerOptions: {
                            thinking: creation.thinking,
                            fastMode: creation.fastMode,
                        },
                    },
                },
                { signal }
            )

            const taskId = turnResult.session.sessionId
            this.store.runtimes.applyTurnUpdate(taskId, {
                turnId: turnResult.turn.turnId,
                sessionId: turnResult.session.sessionId,
                status: turnResult.turn.status,
                queuedAt: turnResult.turn.queuedAt,
                startedAt: turnResult.turn.startedAt,
                completedAt: turnResult.turn.completedAt,
                error: turnResult.turn.error,
            })
            await this.store.applyPendingNestTurnUpdatesForTask(taskId)

            if (signal.aborted) {
                await requestNestMethod("session.turn.cancel", {
                    turnId: turnResult.turn.turnId,
                }).catch((err) => {
                    console.warn("[TaskCreationManager] Failed to cancel Nest turn:", err)
                })
                this.store.runtimes.applyTurnUpdate(taskId, {
                    turnId: turnResult.turn.turnId,
                    sessionId: turnResult.session.sessionId,
                    status: "cancelled",
                    completedAt: new Date().toISOString(),
                })
                throw new Error("Task creation cancelled")
            }

            void this.store.timelines
                .appendDo({
                    taskId,
                    workspaceId: creation.repoId,
                    id: `do.${creation.id}`,
                    commentCount: 0,
                    createdAt: creation.createdAt,
                    anchor: {
                        sessionId: turnResult.session.sessionId,
                        turnId: turnResult.turn.turnId,
                    },
                })
                .catch((error) => {
                    console.warn("[TaskCreationManager] Task was created, but its initial timeline entry could not be recorded", {
                        taskId,
                        turnId: turnResult.turn.turnId,
                        error,
                    })
                })

            this.releaseRecoveryDraft(creation)
            runInAction(() => {
                creation.completedTaskId = taskId
            })
            queueModeManager.notifyTaskSubmitted()

            track("task_created", {
                mode: "do",
                hasMcpServers: false,
            })
        } catch (err) {
            if (err instanceof Error && err.message === "Task creation cancelled") {
                runInAction(() => {
                    this.creationsById.delete(id)
                })
                return
            }
            console.error("[TaskCreationManager] Creation failed:", err)
            runInAction(() => {
                creation.error = err instanceof Error ? err.message : "Failed to create task"
            })
        }
    }

    private restoreRecoveryDraft(creation: TaskCreation): void {
        const editorManager = this.store.smartEditors.getManager("task-create", creation.repoId)
        if (creation.recoveryDraftId) {
            editorManager.popStash(creation.recoveryDraftId)
            return
        }
        void editorManager.restoreCancelledInput(buildTaskCreationInput(creation.description, creation.images))
    }

    private releaseRecoveryDraft(creation: TaskCreation): void {
        if (!creation.recoveryDraftId) return
        this.store.smartEditors.getManager("task-create", creation.repoId).deleteStash(creation.recoveryDraftId)
    }

    private async designateAgentGitContext(creation: TaskCreation, repoPath: string): Promise<TaskAgentGitContext | undefined> {
        try {
            const gitInfo = await this.store.repos.getGitInfo(creation.repoId)
            if (!gitInfo?.isGitRepo) return undefined
            const worktreePath = await getManagedWorktreePath(creation.id)
            const baseDir = await getManagedWorktreeBasePath()
            const summary = await gitApi.getGitSummary({
                workspaceId: creation.repoId,
                workspaceRoot: repoPath,
            })
            return {
                v: 1,
                baseDir,
                worktreePath,
                ...(summary.headCommit ? { startHead: summary.headCommit } : {}),
                deviceId: nestClient.getDiagnostics().nodeId ?? getDeviceId(),
                createdAt: new Date().toISOString(),
            }
        } catch (error) {
            console.warn("[TaskCreationManager] Failed to designate agent worktree path; continuing without it:", error)
            return undefined
        }
    }
}
